import algoliasearchHelper from 'algoliasearch-helper';
import { useEffect } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { useEvents } from '~/shared/hooks/useEvents';
import { useFrame } from '~/shared/utils';

// Active refinements as Algolia `attribute:value` pairs; numeric ranges use Algolia's `min TO max` syntax
const getRefinementFilterPairs = (
    results: Pick<algoliasearchHelper.SearchResults, 'getRefinements'>,
): string[] => {
    const pairs: string[] = [];
    const ranges = new Map<string, { min?: number; max?: number }>();

    results.getRefinements().forEach(({ type, attributeName, name, operator, numericValue }) => {
        if (type === 'exclude') return;

        if (type !== 'numeric') {
            pairs.push(`${attributeName}:${name}`);
            return;
        }

        const range = ranges.get(attributeName) ?? {};
        if (operator.startsWith('>')) range.min = numericValue;
        if (operator.startsWith('<')) range.max = numericValue;
        ranges.set(attributeName, range);
    });

    ranges.forEach(({ min, max }, attributeName) => {
        if (min !== undefined && max !== undefined) pairs.push(`${attributeName}:${min} TO ${max}`);
        else if (min !== undefined) pairs.push(`${attributeName}:>=${min}`);
        else if (max !== undefined) pairs.push(`${attributeName}:<=${max}`);
    });

    return pairs;
};

// Must render inside the products <Index> so `results` are scoped to it
export const AlgoliaSearchTracker = () => {
    const { results } = useInstantSearch();
    const { data: frame } = useFrame();
    const { algoliaSearchEvent } = useEvents(frame);

    useEffect(() => {
        if (!results?.index) return;
        // Skip placeholder results before the first response and the proxy client's error fallback
        if (results.__isArtificial || ('_error' in results && results._error)) return;

        algoliaSearchEvent(results, getRefinementFilterPairs(results));
    }, [results]);

    return null;
};
